import { T } from "@/utils/i18n";
/**
 * Aviso de SINCRONIZAÇÃO PENDENTE — a regra (sem tela, sem plataforma).
 *
 * A idade é a do item MAIS ANTIGO ainda não enviado (SyncService.contarPendenciasSync →
 * `maisAntigaEm`). Escalada:
 *   ≥ 30 min → nível 1: aviso curto (notificação local no app; "push" na tela no web);
 *   > 1 h    → nível 2: diálogo no estilo do informativo;
 *   > 8 h    → nível 3: aviso em tela cheia.
 * Todos levam "Sincronizar agora".
 *
 * Cada nível aparece UMA vez enquanto o atraso continuar nele (ou acima). Se o atraso cai —
 * a fila esvaziou, ou os itens antigos subiram e sobrou um mais novo —, os níveis acima do
 * atual são "rearmados" e voltam a aparecer quando o atraso chegar neles de novo. A tela
 * cheia é a exceção: enquanto o atraso passar de 8 h, ela volta a cada 2 h.
 */

export const AVISO_SYNC_PUSH_MS = 30 * 60 * 1000;
export const AVISO_SYNC_DIALOGO_MS = 60 * 60 * 1000;
export const AVISO_SYNC_TELA_CHEIA_MS = 8 * 60 * 60 * 1000;
/** Depois de "Agora não", a tela cheia volta após este tempo (se ainda houver pendência). */
export const AVISO_SYNC_LEMBRETE_TELA_CHEIA_MS = 2 * 60 * 60 * 1000;

export type NivelAvisoSync = 0 | 1 | 2 | 3;

/** Quando cada nível foi mostrado (ms). */
export type VistosAvisoSync = { 1?: number; 2?: number; 3?: number };

export type ResumoAvisoSync = {
  total: number;
  /** Data (ms) do item pendente mais antigo; null = sem pendência datada. */
  maisAntigaEm: number | null;
};

export type DecisaoAvisoSync = {
  /** Nível do atraso agora (0 = nada a avisar). */
  nivel: NivelAvisoSync;
  idadeMs: number;
  /** Nível a MOSTRAR agora (0 = nada novo). */
  exibir: NivelAvisoSync;
  /** Vistos que continuam valendo (os acima do nível atual foram rearmados). */
  vistos: VistosAvisoSync;
};

export function nivelDoAtraso(idadeMs: number): NivelAvisoSync {
  if (!(idadeMs >= AVISO_SYNC_PUSH_MS)) return 0; // também cobre NaN
  if (idadeMs > AVISO_SYNC_TELA_CHEIA_MS) return 3;
  if (idadeMs > AVISO_SYNC_DIALOGO_MS) return 2;
  return 1;
}

export function decidirAvisoSync(
  vistosAntes: VistosAvisoSync | undefined,
  resumo: ResumoAvisoSync,
  agora: number,
): DecisaoAvisoSync {
  if (!(resumo.total > 0) || resumo.maisAntigaEm == null) {
    return { nivel: 0, idadeMs: 0, exibir: 0, vistos: {} };
  }
  // Relógio do aparelho voltou para antes do item: sem atraso (não alarma à toa).
  const idadeMs = Math.max(0, agora - resumo.maisAntigaEm);
  const nivel = nivelDoAtraso(idadeMs);
  const vistos: VistosAvisoSync = {};
  for (const n of [1, 2, 3] as const) {
    const v = vistosAntes?.[n];
    if (n <= nivel && typeof v === "number") vistos[n] = v;
  }
  let exibir: NivelAvisoSync = 0;
  if (nivel === 3) {
    const v = vistos[3];
    exibir = v == null || agora - v >= AVISO_SYNC_LEMBRETE_TELA_CHEIA_MS ? 3 : 0;
  } else if (nivel > 0 && vistos[nivel as 1 | 2] == null) {
    exibir = nivel;
  }
  return { nivel, idadeMs, exibir, vistos };
}

/**
 * Marca o nível como mostrado. Os de baixo contam como vistos também: quem já viu a tela
 * cheia não recebe o diálogo de 1 h depois.
 */
export function marcarVistoAvisoSync(
  vistos: VistosAvisoSync,
  nivel: NivelAvisoSync,
  agora: number,
): VistosAvisoSync {
  const novo: VistosAvisoSync = { ...vistos };
  for (const n of [1, 2, 3] as const) {
    if (n < nivel && novo[n] == null) novo[n] = agora;
  }
  if (nivel > 0) novo[nivel as 1 | 2 | 3] = agora;
  return novo;
}

/** "há 35 minutos", "há 1 hora e 20 minutos", "há 9 horas", "há 2 dias". */
export function textoAtrasoSync(idadeMs: number): string {
  const min = Math.max(0, Math.floor(idadeMs / 60000));
  if (min < 60) return T("há {min} {v}", { min, v: min === 1 ? T("minuto") : T("minutos") });
  const horas = Math.floor(min / 60);
  if (horas < 24) {
    const resto = min % 60;
    const h = `${horas} ${horas === 1 ? "hora" : "horas"}`;
    return horas < 8 && resto > 0
      ? T("há {h} e {resto} {v}", { h, resto, v: resto === 1 ? T("minuto") : T("minutos") })
      : T("há {h}", { h });
  }
  const dias = Math.floor(horas / 24);
  return T("há {dias} {v}", { dias, v: dias === 1 ? T("dia") : T("dias") });
}

export type ContagemPendencias = {
  pontos: number;
  assinaturas: number;
  rondas: number;
  livros: number;
  outras: number;
};

/** Texto do resultado do "Sincronizar agora" (aviso na tela e toast). */
export function mensagemResultadoSync(r: { restantes: number; semInternet: boolean }): {
  ok: boolean;
  titulo: string;
  texto: string;
} {
  if (r.restantes <= 0)
    return { ok: true, titulo: T("Tudo sincronizado"), texto: T("Seus registros foram enviados.") };
  if (r.semInternet)
    return {
      ok: false,
      titulo: T("Sem conexão com a internet"),
      texto:
        T("Os registros continuam guardados no aparelho e são enviados assim que a conexão voltar."),
    };
  return {
    ok: false,
    titulo: `${r.restantes} ${r.restantes === 1 ? "item ainda pendente" : "itens ainda pendentes"}`,
    texto: T("Nem tudo foi aceito agora. O app tenta de novo sozinho."),
  };
}

/** ["2 batidas de ponto", "1 assinatura de documento", "3 outros envios"]. */
export function descreverPendenciasSync(c: ContagemPendencias): string[] {
  const itens: string[] = [];
  const add = (n: number, um: string, varios: string) => {
    if (n > 0) itens.push(`${n} ${n === 1 ? um : varios}`);
  };
  add(c.pontos, T("batida de ponto"), T("batidas de ponto"));
  add(c.assinaturas, T("assinatura de documento"), T("assinaturas de documento"));
  add(c.rondas, T("registro de ronda"), T("registros de ronda"));
  add(c.livros, T("registro do livro de ocorrências"), T("registros do livro de ocorrências"));
  add(c.outras, "outro envio", "outros envios");
  return itens;
}
